import React, { useState } from 'react';
import { Send } from 'lucide-react';

interface ContactFormProps {
  source: 'homepage' | 'contact-page';
  className?: string;
}

// 🔁 Replace with your deployed Apps Script Web App URL
const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbyYLlml5t8F65GAxPwlCXZet4c40fd43MmcBiyl6gGXnj_cp080RODo7OnzAcxz6bCB/exec';

const ContactForm: React.FC<ContactFormProps> = ({ source, className = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await fetch(GOOGLE_SHEETS_URL, {
        method: 'POST',
        mode: 'no-cors', // ✅ disables CORS enforcement
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, source }),
      });

      // ✅ Assume success, since we can't read response in no-cors mode
      setIsSuccess(true);
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className={`bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6 text-center animate-fade-in transform scale-105 ${className}`}>
        <div className="text-green-600 text-lg font-semibold mb-2 animate-bounce">Thank You! 🎉</div>
        <p className="text-green-700">Your message has been sent successfully. We'll get back to you soon!</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`space-y-6 animate-fade-in-up ${className}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-blue-300 hover:shadow-md transform focus:scale-105"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-blue-300 hover:shadow-md transform focus:scale-105"
          />
        </div>
        <div>
          <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
            Company Name
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-blue-300 hover:shadow-md transform focus:scale-105"    
          />
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 hover:border-blue-300 hover:shadow-md transform focus:scale-105"
          />
        </div>
      </div>
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
          Message *
        </label>
        <textarea
          id="message"
          name="message"
          rows={5}
          value={formData.message}
          onChange={handleChange}
          required
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none hover:border-blue-300 hover:shadow-md transform focus:scale-105"
          placeholder="Tell us about your project requirements..."
        ></textarea>
      </div>

      {error && (
        <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg animate-shake border border-red-200">{error}</div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full md:w-auto bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 hover:shadow-xl"
        aria-label='Submit'
      >
        <span className={isSubmitting ? 'animate-pulse' : ''}>
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </span>
        <Send className={`h-4 w-4 ${isSubmitting ? 'animate-spin' : 'group-hover:translate-x-1 transition-transform duration-300'}`} />
      </button>
    </form>
  );
};

export default ContactForm;
